import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { get } from "../../../lib/http";
import { API } from "../../../config/api";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Filler,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import { AD } from "../../adminUi.js";
import { formatDayLabel } from "../../../listYourShow/scheduleHelpers.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Filler
);

/* -------------------- tiny helpers -------------------- */
const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const cn = (...a) => a.filter(Boolean).join(" ");
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const sum = (arr, key) => arr.reduce((acc, x) => acc + Number(x?.[key] || 0), 0);

/** Parse "31 Jul 2026" style suffix → YYYY-MM-DD */
function parseDateLabelFromType(type = "") {
  const m = String(type || "").match(
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\s*$/i
  );
  if (!m) return "";
  const day = String(m[1]).padStart(2, "0");
  const monMap = {
    jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
    jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
  };
  const mon = monMap[String(m[2]).toLowerCase()];
  if (!mon) return "";
  return `${m[3]}-${mon}-${day}`;
}

function resolveCategoryDate(c) {
  const explicit = String(c?.date || "").trim();
  if (explicit) return explicit;
  return parseDateLabelFromType(c?.name || c?.type || c?.categoryId || "");
}
const formatDDMonYYYY = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = month[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}/${mon}/${yyyy}`;
};
const formatDDMonYYYY_HHMM = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = month[d.getMonth()];
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mon}/${yyyy} ${hh}:${mm}`;
};

/* -------------------- UI atoms -------------------- */
function KPIBlock({ label, primary, hint = null }) {
  return (
    <div className={cn(AD.statCard)}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{primary}</p>
      {hint && <p className="mt-1 text-xs text-emerald-700">{hint}</p>}
    </div>
  );
}
function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / Number(max)) * 100)) : 0;
  return (
    <div className="h-2 w-full bg-slate-200 dark:bg-gray-800 rounded-full overflow-hidden">
      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={cn("py-3 px-4 font-semibold", className)}>{children}</th>;
}
function Td({ children, className = "", ...rest }) {
  return (
    <td className={cn("py-3 px-4 align-middle", className)} {...rest}>
      {children}
    </td>
  );
}

/* -------------------- small helpers for Guests UI -------------------- */
const getInitials = (s) => {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text || "");
    // Optionally show a toast/tooltip in your app
  } catch (e) {
    // ignore
  }
};

/* -------------------- normalizers (server-shape agnostic) -------------------- */
function normalizeStatsResponse(res) {
  const root = res?.data && res.success === undefined ? res.data : res; // allow {success,data:{...}} or direct
  const data = root?.data || root?.stats || root;

  const event = data?.event || { id: data?.eventId, name: data?.eventName };
  const totals = data?.totals || {
    totalTickets: Number(data?.totalTickets ?? 0),
    sold: Number(data?.sold ?? 0),
    remaining: Number(
      data?.remaining ?? Math.max(0, (data?.totalTickets || 0) - (data?.sold || 0))
    ),
    revenue: Number(data?.revenue ?? 0),
    grossRevenue: Number(data?.grossRevenue ?? data?.revenue ?? 0),
    platformFees: Number(data?.platformFees ?? data?.convenienceFee ?? 0),
    gst: Number(data?.gst ?? data?.gstAmount ?? 0),
    netRevenue: Number(
      data?.netRevenue ??
        (Number(data?.grossRevenue ?? data?.revenue ?? 0) -
          Number(data?.platformFees ?? data?.convenienceFee ?? 0) -
          Number(data?.gst ?? data?.gstAmount ?? 0))
    ),
  };

  let categories = [];
  if (Array.isArray(data?.categories)) {
    categories = data.categories.map((c) => ({
      id: c.id || c.categoryId || c.name,
      name: c.name || c.categoryId || "—",
      date: resolveCategoryDate(c),
      price: Number(c.price ?? 0),
      total: Number(c.total ?? c.totalTickets ?? 0),
      sold: Number(c.sold ?? 0),
      remaining: Number(
        c.remaining ?? Math.max(0, (c.total ?? c.totalTickets ?? 0) - (c.sold ?? 0))
      ),
      revenue: Number(c.revenue ?? Number(c.sold ?? 0) * Number(c.price ?? 0)),
    }));
  } else if (Array.isArray(data?.ticketCategories)) {
    categories = data.ticketCategories.map((c) => ({
      id: c.key || c.type || c.categoryId,
      name: c.type || c.categoryId || "—",
      date: resolveCategoryDate(c),
      price: Number(c.price ?? 0),
      total: Number(c.finalQuantity ?? c.quantity ?? 0),
      sold: Number(c.sold ?? 0),
      remaining: Math.max(0, Number(c.finalQuantity ?? c.quantity ?? 0) - Number(c.sold ?? 0)),
      revenue: Number(c.sold ?? 0) * Number(c.price ?? 0),
    }));
  }

  const derivedTotals = {
    totalTickets: totals.totalTickets || sum(categories, "total"),
    sold: totals.sold || sum(categories, "sold"),
    remaining:
      totals.remaining ||
      Math.max(
        0,
        (totals.totalTickets || sum(categories, "total")) -
          (totals.sold || sum(categories, "sold"))
      ),
    revenue: totals.revenue || sum(categories, "revenue"),
    grossRevenue: totals.grossRevenue || totals.revenue || sum(categories, "revenue"),
    platformFees: totals.platformFees || 0,
    gst: totals.gst || 0,
    netRevenue:
      totals.netRevenue ||
      (totals.grossRevenue || totals.revenue || sum(categories, "revenue")) -
        (totals.platformFees || 0) -
        (totals.gst || 0),
  };

  return { event, totals: derivedTotals, categories, eventMeta: data?.eventMeta };
}

function normalizeBookingsResponse(res) {
  const root = res?.data && res.success === undefined ? res.data : res;
  const arr = Array.isArray(root?.bookings)
    ? root.bookings
    : Array.isArray(root)
    ? root
    : Array.isArray(res?.bookings)
    ? res.bookings
    : [];

  const norm = arr.map((b) => {
    const id = b.id || b._id || b.bookingId || b.code || b.bookingCode || cryptoRandomKey();
    const code = b.bookingCode || b.code || b.booking_code || b.reference || "—";
    const time = b.bookingTime || b.createdAt || b.created_at || b.time || b.date || null;

    const qty =
      b.qty ??
      b.quantity ??
      b.ticketQty ??
      b.tickets ??
      (Array.isArray(b.items)
        ? b.items.reduce((n, it) => n + Number(it?.qty || it?.quantity || 0), 0)
        : 0);

    const amount =
      Number(
        b.amount ??
          b.total ??
          b.totalAmount ??
          b.price ??
          (Array.isArray(b.items)
            ? b.items.reduce(
                (sum, it) =>
                  sum +
                  Number(
                    it?.amount ||
                      Number(it?.qty || it?.quantity || 0) * Number(it?.price || 0) ||
                      0
                  ),
                0
              )
            : 0)
      ) || 0;

    const discountValue = Number(b.discountValue ?? b.raw?.discountValue ?? 0) || 0;
    const discountCode = String(b.discountCode || b.raw?.discountCode || "").trim();
    const ticketNetAmount =
      Number(b.ticketNetAmount ?? 0) ||
      Math.max(0, amount - discountValue);
    const finalAmount =
      Number(b.finalAmount ?? b.raw?.finalAmount ?? 0) || ticketNetAmount;

    let cat =
      b.ticketCategory ||
      b.category ||
      b.type ||
      (Array.isArray(b.items)
        ? b.items.map((it) => it?.category || it?.type || it?.name).filter(Boolean).join(", ")
        : "");
    if (!cat && Array.isArray(b.tickets)) {
      cat = b.tickets
        .map((t) => t?.category || t?.type || t?.name)
        .filter(Boolean)
        .join(", ");
    }

    const status = String(b.status || b.paymentStatus || b.bookingStatus || "unknown").toLowerCase();

    // ---- NEW: guestDetails pass-through as ARRAY (gestDetails fallback) ----
    const gdRaw = b.guestDetails ?? b.gestDetails ?? [];
    const guestDetails = Array.isArray(gdRaw) ? gdRaw : gdRaw ? [gdRaw] : [];
    const guestCount = guestDetails.length;

    const ticketDates = Array.isArray(b.ticketDates)
      ? b.ticketDates.map((d) => String(d || "").trim()).filter(Boolean)
      : [];
    if (b.ticketDate && !ticketDates.includes(String(b.ticketDate))) {
      ticketDates.push(String(b.ticketDate).trim());
    }
    if (!ticketDates.length && cat) {
      for (const part of String(cat).split(",")) {
        const parsed = parseDateLabelFromType(part.trim());
        if (parsed && !ticketDates.includes(parsed)) ticketDates.push(parsed);
      }
    }

    return {
      id,
      code,
      time,
      qty: Number(qty || 0),
      amount: Number(amount || 0),
      discountCode,
      discountValue,
      ticketNetAmount: Number(ticketNetAmount || 0),
      finalAmount: Number(finalAmount || 0),
      category: cat || "—",
      ticketDates,
      ticketDate: ticketDates[0] || "",
      status,
      userName: String(b.userName || b.user?.name || b.buyer?.name || "").trim(),
      userEmail: String(b.userEmail || b.user?.email || b.buyer?.email || "").trim(),
      userMobile: String(
        b.userMobile || b.user?.mobile || b.buyer?.mobile || b.user?.phone || ""
      ).trim(),
      paymentId: String(
        b.paymentId || b.transactionId || b.paymentRef || ""
      ).trim(),
      guestDetails, // always array
      guestCount,
      raw: b,
    };
  });

  norm.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
  return norm;
}

function cryptoRandomKey() {
  try {
    const bytes = (window.crypto || window.msCrypto).getRandomValues(new Uint8Array(8));
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return String(Math.random()).slice(2);
  }
}

/* -------------------- Export XLSX (flatten guests + guests sheet) -------------------- */
function exportXLSX(rows, filename = "bookings.xlsx", guestCap = 8) {
  if (!Array.isArray(rows) || rows.length === 0) {
    alert("No bookings to export");
    return;
  }

  // determine maximum guests across rows up to guestCap
  const maxGuests = Math.min(
    guestCap,
    rows.reduce((m, b) => Math.max(m, Array.isArray(b.guestDetails) ? b.guestDetails.length : 0), 0)
  );

  // Build header for Bookings sheet (admin: includes payable with tax/fees)
  const bookingHeaders = [
    "Booking Code",
    "Booking Time",
    "User Name",
    "User Email",
    "User Mobile",
    "Payment ID",
    "Ticket Qty",
    "Amount (INR)",
    "Discount Code",
    "Discount (INR)",
    "Ticket Net (INR)",
    "Total Payable (INR)",
    "Ticket Category",
    "Booking Status",
  ];

  // Add Guest columns: Guest 1 Name, Guest 1 Email, ...
  for (let i = 1; i <= maxGuests; i++) {
    bookingHeaders.push(`Guest ${i} Name`);
    bookingHeaders.push(`Guest ${i} Email`);
    bookingHeaders.push(`Guest ${i} Phone`);
    bookingHeaders.push(`Guest ${i} CompanyName`);
    bookingHeaders.push(`Guest ${i} Designation`);
  }

  // Build rows for Bookings sheet
  const bookingRows = rows.map((b) => {
    const base = {
      "Booking Code": b.code || "",
      "Booking Time": b.time ? new Date(b.time).toISOString() : "",
      "User Name": b.userName || b.user?.name || b.buyer?.name || "",
      "User Email": b.userEmail || b.user?.email || b.buyer?.email || "",
      "User Mobile": b.userMobile || b.user?.mobile || b.buyer?.mobile || "",
      "Payment ID": b.paymentId || b.transactionId || "",
      "Ticket Qty": b.qty ?? "",
      "Amount (INR)": typeof b.amount === "number" ? b.amount : "",
      "Discount Code": b.discountCode || "",
      "Discount (INR)": typeof b.discountValue === "number" ? b.discountValue : 0,
      "Ticket Net (INR)":
        typeof b.ticketNetAmount === "number"
          ? b.ticketNetAmount
          : Math.max(0, Number(b.amount || 0) - Number(b.discountValue || 0)),
      "Total Payable (INR)":
        typeof b.finalAmount === "number" ? b.finalAmount : b.amount ?? "",
      "Ticket Category": b.category || "",
      "Booking Status": b.status || "",
      "Payment Mode": b.paymentMode || "",
      "Payment Status": b.paymentStatus || "",
      "Transaction Id": b.transactionId || "",
    };

    const guestDetails = Array.isArray(b.guestDetails) ? b.guestDetails : [];

    for (let i = 0; i < maxGuests; i++) {
      const g = guestDetails[i] || {};
      base[`Guest ${i + 1} Name`] = g?.name || g?.fullName || g?.guestName || "";
      base[`Guest ${i + 1} Email`] = g?.email || g?.mail || "";
      base[`Guest ${i + 1} Phone`] = g?.phone || g?.mobile || g?.contact || "";
      base[`Guest ${i + 1} CompanyName`] = g?.companyName || g?.organisation || g?.org || "";
      base[`Guest ${i + 1} Designation`] = g?.designation || g?.role || "";
    }

    return base;
  });

  // Build a separate 'Guests' sheet where each row is a guest (useful for CSV-like analysis)
  const guestsFlat = [];
  rows.forEach((b) => {
    const guestDetails = Array.isArray(b.guestDetails) ? b.guestDetails : [];
    guestDetails.forEach((g, idx) => {
      guestsFlat.push({
        "Booking Code": b.code || "",
        "Booking Time": b.time ? new Date(b.time).toISOString() : "",
        "Guest Name": g?.name || g?.fullName || g?.guestName || "",
        "Guest Email": g?.email || g?.mail || "",
        "Guest Phone": g?.phone || g?.mobile || g?.contact || "",
        "Guest Company": g?.companyName || g?.organisation || g?.org || "",
        "Guest Designation": g?.designation || g?.role || "",
      });
    });
  });

  // create workbook and add sheets
  const ws1 = XLSX.utils.json_to_sheet(bookingRows, { header: bookingHeaders });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, "Bookings");

  // add guests sheet only if we have guest rows
  if (guestsFlat.length > 0) {
    const guestHeaders = [
      "Booking Code",
      "Booking Time",
      "Booking ID",
      "Guest Index",
      "Guest Name",
      "Guest Email",
      "Guest Phone",
      "Guest Company",
      "Guest Designation",
    ];
    const ws2 = XLSX.utils.json_to_sheet(guestsFlat, { header: guestHeaders });
    XLSX.utils.book_append_sheet(wb, ws2, "Guests");
  }

  // Autosize columns (best-effort)
  const sheetNames = wb.SheetNames;
  sheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    const range = XLSX.utils.decode_range(ws['!ref'] || "A1");
    const colWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxw = 10;
      for (let R = range.s.r; R <= Math.min(range.e.r, 200); ++R) {
        const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
        if (cell && cell.v != null) {
          const l = String(cell.v).length;
          if (l > maxw) maxw = Math.min(l, 60);
        }
      }
      colWidths.push({ wch: maxw + 2 });
    }
    ws['!cols'] = colWidths;
  });

  XLSX.writeFile(wb, filename);
}

/* -------------------- Charts Section -------------------- */
function ChartsSection({ totals, categories, bookings }) {
  // 1. Donut – Sold vs Remaining
  const donutData = {
    labels: ["Sold", "Remaining"],
    datasets: [
      {
        data: [totals.sold || 0, totals.remaining || 0],
        backgroundColor: ["#10b981", "#e2e8f0"],
        borderColor: ["#059669", "#cbd5e1"],
        borderWidth: 1,
        hoverOffset: 6,
      },
    ],
  };
  const donutOptions = {
    responsive: true,
    cutout: "65%",
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
        },
      },
    },
  };

  // 2. Grouped Bar – Category: Sold vs Available
  const catLabels = categories.map((c) => c.name || c.categoryId || "—");
  const barGroupData = {
    labels: catLabels,
    datasets: [
      {
        label: "Sold",
        data: categories.map((c) => c.sold || 0),
        backgroundColor: "#f59e0b",
        borderRadius: 5,
        borderSkipped: false,
      },
      {
        label: "Available",
        data: categories.map((c) => c.remaining || 0),
        backgroundColor: "#e2e8f0",
        borderRadius: 5,
        borderSkipped: false,
      },
    ],
  };
  const barGroupOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      title: { display: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: "#f1f5f9" }, ticks: { font: { size: 11 } } },
    },
  };

  // 3. Horizontal Bar – Revenue by Category
  const revenueColors = ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#14b8a6"];
  const revData = {
    labels: catLabels,
    datasets: [
      {
        label: "Revenue (₹)",
        data: categories.map((c) => c.revenue || 0),
        backgroundColor: catLabels.map((_, i) => revenueColors[i % revenueColors.length]),
        borderRadius: 5,
        borderSkipped: false,
      },
    ],
  };
  const revOptions = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${Number(ctx.parsed.x || 0).toLocaleString("en-IN")}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          callback: (v) => `₹${Number(v).toLocaleString("en-IN")}`,
        },
        grid: { color: "#f1f5f9" },
      },
      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
    },
  };

  // 4. Line – Bookings over time (group by date)
  const dateMap = {};
  bookings.forEach((b) => {
    if (b.time) {
      const d = new Date(b.time);
      if (!isNaN(d)) {
        const key = d.toISOString().slice(0, 10);
        dateMap[key] = (dateMap[key] || 0) + 1;
      }
    }
  });
  const sortedKeys = Object.keys(dateMap).sort().slice(-14);
  const lineLabels = sortedKeys.map((k) => {
    const d = new Date(k);
    return `${d.getDate()} ${month[d.getMonth()]}`;
  });
  const lineValues = sortedKeys.map((k) => dateMap[k]);
  const lineData = {
    labels: lineLabels,
    datasets: [
      {
        label: "Bookings",
        data: lineValues,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.12)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#f59e0b",
      },
    ],
  };
  const lineOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 10 } },
        grid: { color: "#f1f5f9" },
      },
    },
  };

  const hasCategories = categories.length > 0;
  const hasBookingTimeline = sortedKeys.length >= 2;

  if (!hasCategories) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Donut – Ticket Status */}
      <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-800 dark:text-white mb-4">
          Ticket Status
        </div>
        <div className="flex items-center justify-center">
          <div style={{ width: 220, height: 220 }}>
            <Doughnut data={donutData} options={donutOptions} />
          </div>
        </div>
        <div className="mt-3 flex justify-center gap-6 text-xs text-slate-600 dark:text-slate-400">
          <span>
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1" />
            Sold: <strong>{totals.sold}</strong>
          </span>
          <span>
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-200 mr-1" />
            Remaining: <strong>{totals.remaining}</strong>
          </span>
        </div>
      </div>

      {/* Grouped Bar – Category-wise Sales */}
      <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-800 dark:text-white mb-4">
          Category-wise Sales
        </div>
        <Bar data={barGroupData} options={barGroupOptions} />
      </div>

      {/* Horizontal Bar – Revenue by Category */}
      <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-800 dark:text-white mb-4">
          Revenue by Category
        </div>
        <Bar data={revData} options={revOptions} />
      </div>

      {/* Line – Bookings Over Time */}
      {hasBookingTimeline ? (
        <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-800 dark:text-white mb-4">
            Bookings Over Time
          </div>
          <Line data={lineData} options={lineOptions} />
        </div>
      ) : (
        <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm flex flex-col items-center justify-center text-slate-400 dark:text-slate-600 text-sm gap-2">
          <svg width="36" height="36" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="1.5" d="M3 17l4-4 4 4 4-6 4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>Not enough booking data for timeline</span>
        </div>
      )}
    </div>
  );
}

/* -------------------- Page Component (RESPONSIVE + BETTER GUESTS) -------------------- */
export default function AdminEeventDashboard() {
  const { id: eventId } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState(null);

  const [bLoading, setBLoading] = useState(true);
  const [bErr, setBErr] = useState("");
  const [bookings, setBookings] = useState([]);
  const [eventDatesFromApi, setEventDatesFromApi] = useState([]);
  const [dateFilter, setDateFilter] = useState("all"); // "all" | YYYY-MM-DD

  /* Exhibition flag for conditional Guests column */
  const [isExhibition, setIsExhibition] = useState(false);

  /* -------- pagination state -------- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const eventDates = useMemo(() => {
    const fromCats = (stats?.categories || [])
      .map((c) => resolveCategoryDate(c))
      .filter(Boolean);
    const fromBookings = bookings.flatMap((b) => b.ticketDates || []);
    return [...new Set([...eventDatesFromApi, ...fromCats, ...fromBookings])].sort();
  }, [stats, bookings, eventDatesFromApi]);

  const filteredCategories = useMemo(() => {
    const cats = stats?.categories || [];
    if (dateFilter === "all") return cats;
    return cats.filter((c) => resolveCategoryDate(c) === dateFilter);
  }, [stats, dateFilter]);

  const filteredBookings = useMemo(() => {
    if (dateFilter === "all") return bookings;
    return bookings.filter((b) => (b.ticketDates || []).includes(dateFilter));
  }, [bookings, dateFilter]);

  const filteredTotals = useMemo(() => {
    const base = stats?.totals || {
      totalTickets: 0,
      sold: 0,
      remaining: 0,
      revenue: 0,
      grossRevenue: 0,
      platformFees: 0,
      gst: 0,
      netRevenue: 0,
    };
    if (dateFilter === "all") return base;
    const sold = sum(filteredCategories, "sold");
    const totalTickets = sum(filteredCategories, "total");
    const remaining = sum(filteredCategories, "remaining");
    const revenue = sum(filteredCategories, "revenue");
    return {
      ...base,
      totalTickets,
      sold,
      remaining,
      revenue,
      grossRevenue: revenue,
      // Fees/GST are booking-level; keep overall when filtering by date unless we have booking breakdown
      netRevenue: revenue - Number(base.platformFees || 0) - Number(base.gst || 0),
    };
  }, [stats, dateFilter, filteredCategories]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, filteredBookings.length, dateFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((filteredBookings?.length || 0) / pageSize)),
    [filteredBookings, pageSize]
  );

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredBookings.slice(start, end);
  }, [filteredBookings, page, pageSize]);

  const showingFrom = useMemo(
    () => (filteredBookings.length === 0 ? 0 : (page - 1) * pageSize + 1),
    [page, pageSize, filteredBookings.length]
  );
  const showingTo = useMemo(
    () => Math.min(page * pageSize, filteredBookings.length),
    [page, pageSize, filteredBookings.length]
  );

  const goFirst = () => setPage(1);
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  // fetch stats
  const loadStats = async () => {
    setLoading(true);
    setErr("");
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        API?.events?.eventStats && typeof API.events?.eventStats === "function"
          ? API.events.eventStats(eventId)
          : `/api/admin/events/${eventId}/stats`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      setStats(normalizeStatsResponse(res));
    } catch (e) {
      console.error("stats error:", e);
      setErr(e?.response?.data?.message || e?.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  // fetch bookings (+ set isExhibition)
  const loadBookings = async () => {
    setBLoading(true);
    setBErr("");
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        API?.booking?.eventBookings && typeof API.booking?.eventBookings === "function"
          ? API.booking.eventBookings(eventId)
          : `/api/admin/events/${eventId}/bookings`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      const root = res?.data && res.success === undefined ? res.data : res;
      const eventCategory =
        root?.data?.eventCategory ||
        root?.eventCategory ||
        stats?.event?.category ||
        stats?.eventMeta?.category ||
        "";

      setIsExhibition(String(eventCategory || "").trim().toLowerCase() === "exhibition");

      const dates = root?.data?.eventDates || root?.eventDates || [];
      setEventDatesFromApi(Array.isArray(dates) ? dates.filter(Boolean) : []);
      setBookings(normalizeBookingsResponse(res));
    } catch (e) {
      console.error("bookings error:", e);
      setBErr(e?.response?.data?.message || e?.message || "Failed to load bookings");
    } finally {
      setBLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) return;
    loadStats();
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const totals = filteredTotals;
  const categories = filteredCategories;
  const ev = stats?.event || { id: eventId, name: "Event" };
  const eventMeta = stats?.eventMeta || {};

  const handleExportXLSX = () => {
    exportXLSX(filteredBookings, `bookings_${eventId}.xlsx`, 8); // guestCap default 8
  };

  const DateFilterTabs = () => {
    if (eventDates.length < 2) return null;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Event date
        </span>
        <button
          type="button"
          onClick={() => setDateFilter("all")}
          className={cn(
            "rounded-full px-3 py-1.5 text-xs font-bold transition",
            dateFilter === "all"
              ? "bg-amber-400 text-slate-900 shadow-sm"
              : "border border-slate-200 bg-white text-slate-700 hover:border-amber-300 dark:border-gray-700 dark:bg-gray-900"
          )}
        >
          All dates
        </button>
        {eventDates.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDateFilter(d)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-bold transition",
              dateFilter === d
                ? "bg-amber-400 text-slate-900 shadow-sm"
                : "border border-slate-200 bg-white text-slate-700 hover:border-amber-300 dark:border-gray-700 dark:bg-gray-900"
            )}
          >
            {formatDayLabel(d)}
          </button>
        ))}
      </div>
    );
  };

  // Guests modal
  const [openGuestsFor, setOpenGuestsFor] = useState(null); // booking id or null
  const activeGuests =
    openGuestsFor ? filteredBookings.find((x) => x.id === openGuestsFor)?.guestDetails || [] : [];

  /* -------------------- render -------------------- */
  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div className="min-w-0">
          <nav className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <Link to="/admin" className="hover:text-amber-700">
              Admin
            </Link>
            <span>/</span>
            <Link to="/admin/events" className="hover:text-amber-700">
              Events
            </Link>
            <span>/</span>
            <span className="font-medium text-slate-700">Dashboard</span>
          </nav>
          <h1 className={AD.title}>{ev?.name || "Event"}</h1>
          <p className={AD.subtitle}>
            {[eventMeta?.date && formatDDMonYYYY(eventMeta.date), eventMeta?.time, eventMeta?.venueName, eventMeta?.city]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className={AD.toolbar}>
          <button
            type="button"
            onClick={() => {
              loadStats();
              loadBookings();
            }}
            className={AD.btnSecondary}
          >
            Refresh
          </button>
          <Link to={`/admin/events/${eventId}`} className={AD.btnSecondary}>
            View event
          </Link>
          <Link to="/admin/events" className={AD.btnPrimary}>
            All events
          </Link>
        </div>
      </div>

      {eventDates.length > 1 && (
        <div className="mb-2 rounded-2xl border border-amber-100 bg-amber-50/50 px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
          <DateFilterTabs />
          {dateFilter !== "all" && (
            <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
              Showing sales, charts &amp; bookings for{" "}
              <strong>{formatDayLabel(dateFilter)}</strong> only.
            </p>
          )}
        </div>
      )}

      {/* KPI row */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 text-sm text-slate-600 dark:text-slate-400">Loading analytics…</div>
          ) : err ? (
            <div className="col-span-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
              {err}
            </div>
          ) : (
            <>
              <KPIBlock
                label="Total Tickets Sold"
                primary={`${String(totals.sold).padStart(2, "0")} tickets`}
                hint={totals.soldToday ? `+${totals.soldToday} tickets sold today` : null}
              />
              <KPIBlock label="Remaining Tickets" primary={`${totals.remaining} tickets`} />
              <KPIBlock label="Gross Revenue" primary={inr(totals.grossRevenue || totals.revenue)} />
              <KPIBlock label="Platform Fees" primary={inr(totals.platformFees)} />
              <KPIBlock label="GST" primary={inr(totals.gst)} />
              <KPIBlock
                label="Net Revenue"
                primary={inr(totals.netRevenue)}
                hint="Gross - Platform Fees - GST"
              />
            </>
          )}
        </div>

        {/* Categories */}
        {!loading && !err && (
          <>
            {/* ── Charts ── */}
            <ChartsSection totals={totals} categories={categories} bookings={filteredBookings} />

            {/* ── Category Table ── */}
            <div className="overflow-hidden rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="px-4 py-3 text-sm font-semibold">Ticket Category Wise Sales</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50/70 dark:bg-gray-800/60 text-slate-600 dark:text-slate-300 uppercase text-xs">
                  <tr>
                    <Th className="py-2">Ticket Category</Th>
                    <Th className="py-2">Price Per Ticket</Th>
                    <Th className="py-2">Tickets Sold</Th>
                    <Th className="py-2 text-right">Ticket Sales</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {categories.length === 0 && (
                    <tr>
                      <Td colSpan={4} className="py-6 text-center text-slate-500">
                        No category data
                      </Td>
                    </tr>
                  )}
                  {categories.map((c) => {
                    const sold = Number(c.sold || 0);
                    const total = Number(c.total || 0);
                    const soldToday = Number(c.soldToday || 0);
                    return (
                      <tr
                        key={c.id || c.categoryId || c.name}
                        className="hover:bg-amber-50/40 dark:hover:bg-gray-800/30"
                      >
                        <Td className="whitespace-nowrap">{c.name || c.categoryId || "—"}</Td>
                        <Td className="whitespace-nowrap">{inr(c.price)}</Td>
                        <Td className="min-w-[220px]">
                          <div className="flex items-center gap-2">
                            <div className="w-28">
                              <ProgressBar value={sold} max={total} />
                            </div>
                            <div className="font-mono text-xs">
                              {sold}/{total}
                            </div>
                            {soldToday > 0 && (
                              <span className="ml-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                +{soldToday} today
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td className="text-right font-semibold">{inr(c.revenue)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
                {categories.length > 0 && (
                  <tfoot className="bg-slate-50/70 dark:bg-gray-800/60">
                    <tr>
                      <Td className="font-semibold">Totals</Td>
                      <Td />
                      <Td className="font-semibold">
                        {sum(categories, "sold")}/{sum(categories, "total")}
                      </Td>
                      <Td className="text-right font-bold">{inr(sum(categories, "revenue"))}</Td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          </>
        )}

        {/* Bookings - responsive: table on md+, cards on small */}
        <div className="overflow-hidden rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="text-sm font-semibold">
              Bookings{" "}
              {bLoading ? (
                ""
              ) : (
                <span className="text-slate-500 font-normal">
                  • Total Bookings: {filteredBookings.length}
                  {dateFilter !== "all" && bookings.length !== filteredBookings.length
                    ? ` (of ${bookings.length})`
                    : ""}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Rows per page</span>
                <select
                  className="rounded-lg border px-2 py-1 bg-white dark:bg-gray-900 text-xs"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <button
                onClick={handleExportXLSX}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-amber-900/20"
                title="Export to Excel (.xlsx)"
              >
                Export XLSX
              </button>
              <div className="text-xs text-slate-500">Shows latest first</div>
            </div>
          </div>

          {/* Loading / error / empty */}
          {bLoading ? (
            <div className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-400">Loading bookings…</div>
          ) : bErr ? (
            <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">{bErr}</div>
          ) : filteredBookings.length === 0 ? (
            <div className="m-4 rounded-xl border dark:border-gray-800 p-4 text-sm text-slate-500">
              {bookings.length === 0
                ? "No bookings yet."
                : `No bookings for ${formatDayLabel(dateFilter)}.`}
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE (md+) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50/70 dark:bg-gray-800/60 text-slate-600 dark:text-slate-300 uppercase text-xs">
                    <tr>
                      <Th className="py-2">Booking Code</Th>
                      <Th className="py-2">Booking Time</Th>
                      <Th className="py-2">User Detail</Th>
                      <Th className="py-2">Payment ID</Th>
                      <Th className="py-2">Ticket Qty</Th>
                      <Th className="py-2">Amount</Th>
                      <Th className="py-2">Discount</Th>
                      <Th className="py-2">Ticket Net</Th>
                      <Th className="py-2">Total Payable</Th>
                      <Th className="py-2">Ticket Category</Th>
                      <Th className="py-2">Booking Status</Th>
                      {isExhibition && <Th className="py-2">Guests</Th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                    {paginatedBookings.map((b) => (
                      <tr key={b.id} className="hover:bg-amber-50/40 dark:hover:bg-gray-800/30">
                        <Td className="font-mono">{b.code || "—"}</Td>
                        <Td>{formatDDMonYYYY_HHMM(b.time)}</Td>
                        <Td>
                          <div className="min-w-[160px] max-w-[220px] space-y-0.5 leading-tight normal-case">
                            <div className="font-semibold text-slate-800 dark:text-slate-100 truncate" title={b.userName || ""}>
                              {b.userName || "—"}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate" title={b.userEmail || ""}>
                              {b.userEmail || "—"}
                            </div>
                            <div className="font-mono text-[11px] text-slate-500">
                              {b.userMobile || "—"}
                            </div>
                          </div>
                        </Td>
                        <Td
                          className="max-w-[140px] truncate font-mono text-xs"
                          title={b.paymentId || ""}
                        >
                          {b.paymentId || "—"}
                        </Td>
                        <Td>{b.qty}</Td>
                        <Td className="font-semibold">{inr(b.amount)}</Td>
                        <Td>
                          {Number(b.discountValue) > 0 || b.discountCode ? (
                            <div className="leading-tight">
                              <div className="font-semibold text-emerald-700 dark:text-emerald-400">
                                − {inr(b.discountValue || 0)}
                              </div>
                              {b.discountCode ? (
                                <div className="text-[11px] font-mono text-slate-500">
                                  {b.discountCode}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </Td>
                        <Td className="font-semibold">
                          {inr(
                            Number(
                              b.ticketNetAmount ??
                                Math.max(0, Number(b.amount || 0) - Number(b.discountValue || 0))
                            )
                          )}
                        </Td>
                        <Td className="font-semibold">
                          {inr(
                            Number(b.finalAmount) > 0
                              ? b.finalAmount
                              : Math.max(0, Number(b.amount || 0) - Number(b.discountValue || 0))
                          )}
                        </Td>
                        <Td className="whitespace-nowrap">{b.category || "—"}</Td>
                        <Td>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                              b.status === "paid" || b.status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : b.status === "pending"
                                ? "bg-amber-100 text-amber-800"
                                : b.status === "cancelled" || b.status === "failed"
                                ? "bg-rose-100 text-rose-700"
                                : "bg-slate-200 text-slate-700"
                            )}
                          >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                            {b.status}
                          </span>
                        </Td>

                        {isExhibition && (
                          <Td>
                            <div className="flex items-center gap-2">
                              {/* Avatar stack: show up to 3 avatars, then +N */}
                              <div className="flex -space-x-2">
                                {Array.isArray(b.guestDetails) && b.guestDetails.slice(0, 3).map((g, i) => (
                                  <div key={i} className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-gray-900 overflow-hidden bg-slate-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold">
                                    {getInitials(g?.name || g?.fullName || g?.companyName)}
                                  </div>
                                ))}
                              </div>

                              <div className="text-xs text-slate-600 dark:text-slate-400 font-semibold">
                                {b.guestCount ?? (Array.isArray(b.guestDetails) ? b.guestDetails.length : 0)}
                              </div>

                              {b.guestDetails?.length > 0 && (
                                <button
                                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                                  onClick={() => setOpenGuestsFor(b.id)}
                                >
                                  View
                                </button>
                              )}
                            </div>
                          </Td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS (smaller screens) */}
              <div className="md:hidden space-y-3 px-3 pb-4">
                {paginatedBookings.map((b) => (
                  <article key={b.id} className="rounded-lg border dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-xs text-slate-700 dark:text-slate-200">{b.code || "—"}</div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">{formatDDMonYYYY_HHMM(b.time)}</div>
                        </div>

                        <div className="mt-1.5 text-xs text-slate-700 dark:text-slate-200">
                          <div className="font-semibold truncate">{b.userName || "—"}</div>
                          <div className="truncate text-[11px] text-slate-500">{b.userEmail || "—"}</div>
                          <div className="font-mono text-[11px] text-slate-500">{b.userMobile || "—"}</div>
                          {b.paymentId ? (
                            <div className="mt-0.5 truncate font-mono text-[10px] text-slate-400" title={b.paymentId}>
                              Pay: {b.paymentId}
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-2 flex items-start gap-3">
                          <div className="flex flex-col text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-semibold text-slate-900 dark:text-white">
                              {inr(
                                Number(b.finalAmount) > 0
                                  ? b.finalAmount
                                  : Math.max(0, Number(b.amount || 0) - Number(b.discountValue || 0))
                              )}
                            </span>
                            <span className="text-[11px]">{b.qty} • {b.category || "—"}</span>
                            <span className="mt-0.5 text-[11px] text-slate-500">
                              Ticket net{" "}
                              {inr(
                                Number(
                                  b.ticketNetAmount ??
                                    Math.max(0, Number(b.amount || 0) - Number(b.discountValue || 0))
                                )
                              )}
                              {Number(b.discountValue) > 0 || b.discountCode
                                ? ` · Disc −${inr(b.discountValue || 0)}${
                                    b.discountCode ? ` (${b.discountCode})` : ""
                                  }`
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* status & guests */}
                      <div className="flex flex-col items-end gap-2">
                        <div>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold",
                              b.status === "paid" || b.status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : b.status === "pending"
                                ? "bg-amber-100 text-amber-800"
                                : b.status === "cancelled" || b.status === "failed"
                                ? "bg-rose-100 text-rose-700"
                                : "bg-slate-200 text-slate-700"
                            )}
                          >
                            {b.status}
                          </span>
                        </div>

                        {isExhibition && (
                          <div className="flex items-center gap-2">
                            <div className="flex -space-x-2">
                              {Array.isArray(b.guestDetails) && b.guestDetails.slice(0, 2).map((g, i) => (
                                <div key={i} className="w-7 h-7 rounded-full ring-2 ring-white dark:ring-gray-900 overflow-hidden bg-slate-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold">
                                  {getInitials(g?.name || g?.fullName || g?.companyName)}
                                </div>
                              ))}
                            </div>

                            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                              {b.guestCount ?? (Array.isArray(b.guestDetails) ? b.guestDetails.length : 0)}
                            </div>

                            {b.guestDetails?.length > 0 && (
                              <button
                                className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                                onClick={() => setOpenGuestsFor(b.id)}
                              >
                                View
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* pagination footer */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t dark:border-gray-800">
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  Showing <span className="font-semibold">{showingFrom}</span>–
                  <span className="font-semibold">{showingTo}</span> of{" "}
                  <span className="font-semibold">{filteredBookings.length}</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goFirst}
                    disabled={page <= 1}
                    aria-label="First page"
                  >
                    « First
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goPrev}
                    disabled={page <= 1}
                    aria-label="Previous page"
                  >
                    ‹ Prev
                  </button>

                  <span className="px-2 text-xs">
                    Page <span className="font-semibold">{page}</span> / {totalPages}
                  </span>

                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goNext}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                  >
                    Next ›
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goLast}
                    disabled={page >= totalPages}
                    aria-label="Last page"
                  >
                    Last »
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Guests modal (structured fields, no JSON) */}
      {isExhibition && openGuestsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpenGuestsFor(null)} />
          <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Guest Details</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    // copy a CSV-like text of guests to clipboard
                    const csv = activeGuests.map((g, i) => {
                      return [
                        i + 1,
                        g?.name || g?.fullName || "",
                        g?.email || "",
                        g?.phone || "",
                        g?.companyName || "",
                        g?.designation || ""
                      ].map(x => `"${String(x || "").replace(/"/g, '""')}"`).join(",");
                    }).join("\n");
                    copyToClipboard(csv);
                  }}
                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                  title="Copy guests as CSV"
                >
                  Copy CSV
                </button>

                <button
                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                  onClick={() => setOpenGuestsFor(null)}
                >
                  Close
                </button>
              </div>
            </div>

            {activeGuests.length === 0 ? (
              <div className="text-sm text-slate-500">No guests</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-auto">
                {activeGuests.map((g, i) => (
                  <div key={i} className="rounded-lg border dark:border-gray-800 p-3 bg-slate-50 dark:bg-gray-800/40">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center text-sm font-semibold">
                        {getInitials(g?.name || g?.fullName || g?.companyName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm text-slate-900 dark:text-white">
                            {g?.name || g?.fullName || g?.companyName || `Guest #${i + 1}`}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{g?.designation || ""}</div>
                        </div>

                        {g?.companyName && <div className="text-xs text-slate-600 dark:text-slate-400">Company: {g.companyName}</div>}

                        {g?.email && (
                          <div className="mt-2 flex items-center gap-2">
                            <a href={`mailto:${g.email}`} className="text-xs text-amber-600 hover:underline">
                              {g.email}
                            </a>
                            <button
                              onClick={() => copyToClipboard(g.email)}
                              className="text-[11px] rounded border px-2 py-0.5"
                              title="Copy email"
                            >
                              Copy
                            </button>
                          </div>
                        )}

                        {g?.phone && (
                          <div className="mt-2 flex items-center gap-2">
                            <a href={`tel:${g.phone}`} className="text-xs text-amber-600 hover:underline">
                              {g.phone}
                            </a>
                            <button
                              onClick={() => copyToClipboard(g.phone)}
                              className="text-[11px] rounded border px-2 py-0.5"
                              title="Copy mobile"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
